import { useState } from 'react'
import { db } from 'config/firebase'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DamageReport } from '@otw/models'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { urlToFile } from '@/core/utils/fileUtils'
import { portalApi } from '@/core/api/modules/portalApi'

export interface DamageReportDocument {
  id: string
  photoId: string
  report: DamageReport
  createdAt: string
  updatedAt: string
}

export const useDamageReports = (photoId?: string) => {
  const queryClient = useQueryClient()
  const [isInspectingPhoto, setIsInspectingPhoto] = useState(false)
  const [inspectionError, setInspectionError] = useState<string | null>(null)
  const { showError } = useSnackbar()

  const {
    data: damageReport,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['damageReport', photoId],
    queryFn: async () => {
      if (!photoId) return null
      const docRef = doc(db, 'damageReports', photoId)
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      return docSnap.data() as DamageReportDocument
    },
    enabled: !!photoId,
    staleTime: 5 * 60 * 1000,
  })

  const saveDamageReportMutation = useMutation({
    mutationFn: async ({ photoId, report }: { photoId: string; report: DamageReport }) => {
      const docRef = doc(db, 'damageReports', photoId)
      const now = new Date().toISOString()
      const existing = await getDoc(docRef)
      const createdAt = existing.exists()
        ? ((existing.data() as DamageReportDocument).createdAt ?? now)
        : now
      const damageReportDoc: DamageReportDocument = {
        id: photoId,
        photoId,
        report,
        createdAt,
        updatedAt: now,
      }
      await setDoc(docRef, damageReportDoc)
      return damageReportDoc
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['damageReport', data.photoId] })
      queryClient.invalidateQueries({ queryKey: ['damageReportsBatch'] })
    },
  })

  const generateAndSaveDamageReport = async (
    photoId: string,
    photoUrl: string,
    angle: string,
    options?: { silent?: boolean }
  ) => {
    if (isInspectingPhoto) throw new Error('Already inspecting a photo')
    setIsInspectingPhoto(true)
    setInspectionError(null)
    try {
      const file = await urlToFile(photoUrl, `${angle}_${photoId}`)
      const result = await portalApi.admin.inspectVehicleDamage(file)
      await saveDamageReportMutation.mutateAsync({ photoId, report: result })
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to inspect photo'
      setInspectionError(errorMessage)
      if (!options?.silent) {
        showError(`Error inspecting photo: ${errorMessage}`)
      }
      throw error
    } finally {
      setIsInspectingPhoto(false)
    }
  }

  const clearError = () => setInspectionError(null)

  return {
    damageReport: damageReport?.report || null,
    isLoading,
    error,
    saveDamageReport: saveDamageReportMutation.mutate,
    isSaving: saveDamageReportMutation.isPending,
    generateAndSaveDamageReport,
    isInspectingPhoto,
    inspectionError,
    clearError,
  }
}

/** Batch-load damage reports keyed by photo id (for grid chips). */
export const useDamageReportsByPhotoIds = (photoIds: string[]) => {
  const sortedIds = [...new Set(photoIds.filter(Boolean))].sort()
  const sortedKey = sortedIds.join(',')

  return useQuery({
    queryKey: ['damageReportsBatch', sortedKey],
    queryFn: async (): Promise<Record<string, DamageReport | null>> => {
      const results: Record<string, DamageReport | null> = {}
      await Promise.all(
        sortedIds.map(async (id) => {
          const snap = await getDoc(doc(db, 'damageReports', id))
          results[id] = snap.exists() ? (snap.data() as DamageReportDocument).report : null
        })
      )
      return results
    },
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}
